import { SitesType } from '@atlassiansox/asc-stargate';
import {
  AsyncResult,
  Environment,
  getFirstName,
  getLastName,
  ProductKey,
  Url,
  useAISAvailableProducts,
  useMe,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import { useEffect, useState } from 'react';
import { UserDataType } from '../model';
import { isResponseError } from '../utility';

/**
 * Hook that provides user info relevant to signup
 */

export const useUserInfo = () => {
  const [userInfo, setUserInfo] = useState<UserDataType>();

  const meDataAsync = useMe();
  const aisAvailableProducts = useAISAvailableProducts({
    includeInvalidBitbucketSite: true
  });

  const stargateClient = useStargateClient();

  useEffect(() => {
    if (
      AsyncResult.isPending(meDataAsync) ||
      AsyncResult.isPending(aisAvailableProducts)
    ) {
      return;
    }

    const meData = meDataAsync.catch(() => null).value;

    // check XSRF for logged out user, redirect to identity if 401 - in response to HOT-102965
    if (!meData) {
      stargateClient.getSignupServerXsrfToken().catch((e) => {
        if (isResponseError(e) && e.response.status === 401) {
          const identityQueryParams = new URLSearchParams({
            continue: Url.getUrl()
          });
          const redirectUrl = `${Environment.getIdentityBaseUrl()}/login?${identityQueryParams}`;
          window.location.assign(redirectUrl);
        }
      });
    }

    const aisAvailableProductsResult = aisAvailableProducts.catch(
      () => null
    ).value;
    let sitesData = aisAvailableProductsResult?.sites;
    const isNewUser = aisAvailableProductsResult?.isNewUser;
    let name = meData?.name;
    if (name !== undefined && name.includes('@')) {
      name = name.split('@')[0];
    }
    // Customer has BB if they have adminAccess to a site with BB
    const hasBitbucket = (sitesData || [])
      .filter((site: SitesType) => site?.adminAccess)
      .some((site) => {
        return site.products.some((product) => {
          return product === ProductKey.BITBUCKET;
        });
      });

    sitesData =
      sitesData &&
      sitesData.filter((site) => {
        return !['bitbucket', 'Bitbucket'].includes(site.displayName);
      });

    setUserInfo({
      isLoggedIn: !!meData,
      email: meData?.email || null,
      fullName: meData?.name || null,
      firstName: getFirstName(name) || undefined,
      lastName: getLastName(name) || null,
      sites: sitesData || null,
      extendedTrialOverridableSites: null,
      picture: meData?.picture || null,
      accountId: meData?.accountId || null,
      hasBitbucket,
      isNewUser: !!isNewUser
    });
  }, [meDataAsync, aisAvailableProducts]);

  return userInfo;
};
